import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminOnly from "#/components/auth/AdminOnly";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import DetailSection from "#/components/shared/details/DetailSection";
import { formatDate } from "#/components/utls/time";
import useArticleReviewsList from "#/admin/article-reviews/hooks/useArticleReviewsList";
import useArticlesList from "#/admin/articles/hooks/useArticlesList";
import { ArticleReview } from "#/services/articleReviews";
import useArticleDetail from "#/admin/articles/hooks/useArticleDetail";
import BlockRenderer from "#/admin/articles/renderers/BlockRenderer";
import useArticleReviewMutations from "#/admin/article-reviews/hooks/useArticleReviewMutations";
import useArticleReviewRevision from "#/admin/articles/hooks/useArticleReviewRevision";

function sortReviews(items: ArticleReview[]) {
  return [...items].sort((a, b) => {
    const aDate = new Date(a.created_at || 0).getTime();
    const bDate = new Date(b.created_at || 0).getTime();
    return bDate - aDate;
  });
}

type ReviewHighlightTone = "terracotta" | "brown" | "beige";

const reviewPalette: ReviewHighlightTone[] = ["terracotta", "brown", "beige"];

export default function AdminArticleReviewsByArticlePage() {
  const navigate = useNavigate();
  const { articleId } = useParams<{ articleId: string }>();
  const listQuery = useArticleReviewsList({ articleId });
  const articlesQuery = useArticlesList();
  const reviewMutations = useArticleReviewMutations({ articleId });
  const reviewRevisionMutation = useArticleReviewRevision();
  const [pollUntil, setPollUntil] = useState<number | null>(null);

  const article = useMemo(() => {
    if (!articleId || !Array.isArray(articlesQuery.data)) return undefined;
    return articlesQuery.data.find((item) => item.id === articleId);
  }, [articleId, articlesQuery.data]);

  const detailQuery = useArticleDetail(article?.slug);

  const reviews = useMemo(() => {
    const items = Array.isArray(listQuery.data) ? listQuery.data : [];
    return sortReviews(items);
  }, [listQuery.data]);

  const articleLevelReviews = useMemo(
    () =>
      reviews.filter((review) => !review.section_id && !review.block_id),
    [reviews]
  );
  const scopedReviews = useMemo(
    () =>
      reviews.filter((review) => review.section_id || review.block_id),
    [reviews]
  );

  const reviewHighlights = useMemo(() => {
    const blocks: Record<string, ReviewHighlightTone> = {};
    const sections: Record<string, ReviewHighlightTone> = {};
    const seen = new Set<string>();
    let index = 0;

    scopedReviews.forEach((review) => {
      const key = review.block_id || review.section_id;
      if (!key || seen.has(key)) return;
      seen.add(key);
      const tone = reviewPalette[index % reviewPalette.length];
      index += 1;
      if (review.block_id) {
        blocks[review.block_id] = tone;
      } else if (review.section_id) {
        sections[review.section_id] = tone;
      }
    });

    return { blocks, sections };
  }, [scopedReviews]);

  useRevisionPolling(
    pollUntil,
    () => {
      detailQuery.refetch();
      listQuery.refetch();
    },
    () => setPollUntil(null)
  );

  if (!articleId) {
    return (
      <AdminOnly>
        <div className="container mx-auto px-4 py-6">
          <Typography variant="h4" className="text-terracotta">
            Article not found
          </Typography>
        </div>
      </AdminOnly>
    );
  }

  return (
    <AdminOnly>
      <div className="container mx-auto flex flex-col gap-6 px-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <Typography variant="caption" className="text-brown-500">
              Article reviews / {article?.title || article?.slug || articleId}
            </Typography>
            <Typography variant="h2" className="text-brown-900">
              {article?.title || "Article reviews"}
            </Typography>
          </div>
          <ActionButton
            tone="secondary"
            onClick={() => navigate("/admin/content?tab=reviews")}
          >
            Back to reviews
          </ActionButton>
          <ActionButton
            onClick={async () => {
              if (!articleId) return;
              await reviewRevisionMutation.mutateAsync(articleId);
              setPollUntil(Date.now() + 180_000);
            }}
            isLoading={reviewRevisionMutation.isPending}
            disabled={!articleId}
          >
            Submit for revisions
          </ActionButton>
        </div>

        {pollUntil ? (
          <Typography variant="body2" className="text-brown-600">
            Refreshing article content while revisions process…
          </Typography>
        ) : null}

        {listQuery.isLoading ? (
          <Typography variant="body2" className="text-brown-600">
            Loading reviews…
          </Typography>
        ) : listQuery.isError ? (
          <Typography variant="body2" className="text-terracotta">
            {listQuery.error instanceof Error
              ? listQuery.error.message
              : "Unable to load reviews."}
          </Typography>
        ) : reviews.length === 0 ? (
          <Typography variant="body2" className="text-brown-600">
            No reviews found for this article.
          </Typography>
        ) : (
          <div className="flex flex-col gap-6">
            <DetailSection
              title="Article content"
              description="Article-level reviews appear above the content."
              contentClassName="space-y-6"
            >
              {detailQuery.isLoading ? (
                <Typography variant="body2" className="text-brown-600">
                  Loading article content…
                </Typography>
              ) : detailQuery.isError ? (
                <Typography variant="body2" className="text-terracotta">
                  {detailQuery.error instanceof Error
                    ? detailQuery.error.message
                    : "Unable to load article content."}
                </Typography>
              ) : detailQuery.data ? (
                <BlockRenderer
                  payload={detailQuery.data}
                  reviewCards={{
                    article: articleLevelReviews.map((review) => ({
                      id: review.id,
                      notes: review.notes,
                      status: review.status,
                      reviewerId: review.reviewer_id ?? undefined,
                      createdAt: formatDate(review.created_at),
                      tone: "beige",
                      onUpdateNotes: async (id: string, notes: string) => {
                        await reviewMutations.updateReview.mutateAsync({
                          id,
                          payload: { notes },
                        });
                      },
                      onDelete: async (id: string) => {
                        await reviewMutations.removeReview.mutateAsync(id);
                      },
                      isUpdating:
                        reviewMutations.updateReview.isPending &&
                        reviewMutations.updateReview.variables?.id === review.id,
                      isDeleting:
                        reviewMutations.removeReview.isPending &&
                        reviewMutations.removeReview.variables === review.id,
                    })),
                    sections: scopedReviews.reduce((acc, review) => {
                      if (!review.section_id || review.block_id) return acc;
                      const tone =
                        reviewHighlights.sections[review.section_id] || "beige";
                      const isUpdating =
                        reviewMutations.updateReview.isPending &&
                        reviewMutations.updateReview.variables?.id === review.id;
                      const isDeleting =
                        reviewMutations.removeReview.isPending &&
                        reviewMutations.removeReview.variables === review.id;
                      acc[review.section_id] = [
                        ...(acc[review.section_id] ?? []),
                        {
                          id: review.id,
                          notes: review.notes,
                          status: review.status,
                          reviewerId: review.reviewer_id ?? undefined,
                          createdAt: formatDate(review.created_at),
                          tone,
                          onUpdateNotes: async (id: string, notes: string) => {
                            await reviewMutations.updateReview.mutateAsync({
                              id,
                              payload: { notes },
                            });
                          },
                          onDelete: async (id: string) => {
                            await reviewMutations.removeReview.mutateAsync(id);
                          },
                          isUpdating,
                          isDeleting,
                        },
                      ];
                      return acc;
                    }, {} as Record<string, any[]>),
                    blocks: scopedReviews.reduce((acc, review) => {
                      if (!review.block_id) return acc;
                      const tone =
                        reviewHighlights.blocks[review.block_id] || "beige";
                      const isUpdating =
                        reviewMutations.updateReview.isPending &&
                        reviewMutations.updateReview.variables?.id === review.id;
                      const isDeleting =
                        reviewMutations.removeReview.isPending &&
                        reviewMutations.removeReview.variables === review.id;
                      acc[review.block_id] = [
                        ...(acc[review.block_id] ?? []),
                        {
                          id: review.id,
                          notes: review.notes,
                          status: review.status,
                          reviewerId: review.reviewer_id ?? undefined,
                          createdAt: formatDate(review.created_at),
                          tone,
                          onUpdateNotes: async (id: string, notes: string) => {
                            await reviewMutations.updateReview.mutateAsync({
                              id,
                              payload: { notes },
                            });
                          },
                          onDelete: async (id: string) => {
                            await reviewMutations.removeReview.mutateAsync(id);
                          },
                          isUpdating,
                          isDeleting,
                        },
                      ];
                      return acc;
                    }, {} as Record<string, any[]>),
                  }}
                />
              ) : (
                <Typography variant="body2" className="text-brown-600">
                  Article content is unavailable.
                </Typography>
              )}
            </DetailSection>
          </div>
        )}
      </div>
    </AdminOnly>
  );
}

function useRevisionPolling(
  pollUntil: number | null,
  onTick: () => void,
  onStop: () => void
) {
  useEffect(() => {
    if (!pollUntil) return;
    const intervalId = window.setInterval(() => {
      onTick();
    }, 8000);
    return () => window.clearInterval(intervalId);
  }, [pollUntil, onTick]);

  useEffect(() => {
    if (!pollUntil) return;
    const timeoutId = window.setTimeout(() => {
      onStop();
    }, Math.max(0, pollUntil - Date.now()));
    return () => window.clearTimeout(timeoutId);
  }, [pollUntil, onStop]);
}
